import { Controller } from '@hotwired/stimulus'
import consumer from '~/channels/consumer'
import { createLogger } from '~/javascript/services/page_editor/logger.js'

// Phase 5: Shared logger with debug gating
const logger = createLogger('PageEditor')

export default class extends Controller {
  static targets = ['blocksContainer', 'editorSidebar', 'editorContent', 'blockPickerModal', 'aiModal', 'aiPromptInput']

  static values = {
    websiteId: String,
    pageId: String,
    blocks: Array,
    csrf: String,
    regenerateUrl: String,
    contentUrl: String,
    editUrl: String,
    rollbackUrl: String
  }

  connect() {
    logger.log('Controller connected - NEW CODE v2')
    this.setupDragAndDrop()
    this.editingBlockId = null
    this.aiTargetBlockId = null
    this.aiMode = 'edit' // Default to edit mode ('edit' or 'generate')
    this.lastChangedBlock = null // Tracks the last block that was changed for undo
    this.insertContext = null // Tracks insert before/after context { blockId, direction }
    this.updateUndoButton()
    this.setupActionCable()

    // AI Processing state management
    this.processingBlocks = new Map() // blockId -> { mode, startTime }
    this.iframeStylesInjected = false
    this.setupIframeObserver()
    this.startTimeoutMonitor()
    logger.log('Controller initialization complete')
  }

  disconnect() {
    if (this.subscription) {
      this.subscription.unsubscribe()
    }
    if (this.timeoutInterval) {
      clearInterval(this.timeoutInterval)
    }
  }

  setupActionCable() {
    this.subscription = consumer.subscriptions.create(
      { channel: 'WebsiteGenerationChannel', website_id: this.websiteIdValue },
      {
        received: (data) => this.handleCableMessage(data)
      }
    )
  }

  handleCableMessage(data) {
    switch (data.event) {
      case 'block_regeneration_started':
      case 'content_regeneration_started':
      case 'block_edit_started':
        // Already showing loading state from button click
        break
      case 'block_regeneration_completed':
      case 'content_regeneration_completed':
      case 'block_edit_completed':
        this.handleBlockRegenerationComplete(data)
        break
      case 'block_regeneration_failed':
      case 'content_regeneration_failed':
      case 'block_edit_failed':
        this.handleBlockRegenerationFailed(data)
        break
    }
  }

  handleBlockRegenerationComplete(data) {
    // Store for undo
    this.lastChangedBlock = {
      blockId: data.block_id,
      previousVersion: data.version - 1
    }
    this.updateUndoButton()

    // Update block in our local state
    this.blocksValue = this.blocksValue.map((b) => {
      if (b.id === data.block_id) {
        return { ...b, content: data.content, content_version: data.version, has_history: true }
      }
      return b
    })

    // Update block card display
    this.updateBlockCard({ id: data.block_id, content: data.content })

    // End processing animation with success effect
    this.endBlockProcessingWithSuccess(data.block_id)

    // Show appropriate message based on mode
    const messages = {
      edit: 'Block edited with AI',
      content: 'Content updated with AI',
      generate: 'Block regenerated with AI'
    }
    this.showNotification(messages[this.aiMode] || 'Block updated', 'success')
  }

  handleBlockRegenerationFailed(data) {
    // End processing animation with error effect
    this.endBlockProcessingWithError(data.block_id, data.error || 'Failed to generate content')
  }

  resetGenerateButton() {
    const generateBtn = document.getElementById('ai-generate-btn')
    if (generateBtn) {
      const buttonConfig = {
        edit: {
          icon: '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>',
          text: 'Apply Changes'
        },
        content: {
          icon: '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>',
          text: 'Update Content'
        },
        generate: {
          icon: '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"/>',
          text: 'Generate'
        }
      }
      const config = buttonConfig[this.aiMode] || buttonConfig.edit
      generateBtn.innerHTML = `
        <svg id="ai-btn-icon" class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          ${config.icon}
        </svg>
        <span id="ai-btn-text">${config.text}</span>
      `
      generateBtn.disabled = false
    }
  }

  // ==========================================
  // Drag and Drop
  // ==========================================

  setupDragAndDrop() {
    // Make block templates draggable
    document.querySelectorAll('.block-template').forEach((template) => {
      template.addEventListener('dragstart', this.handleTemplateDragStart.bind(this))
      template.addEventListener('dragend', this.handleDragEnd.bind(this))
    })

    // Make existing blocks draggable
    this.setupBlockDragging()

    // Setup drop zone
    const container = this.blocksContainerTarget
    container.addEventListener('dragover', this.handleDragOver.bind(this))
    container.addEventListener('drop', this.handleDrop.bind(this))
    container.addEventListener('dragleave', this.handleDragLeave.bind(this))
  }

  setupBlockDragging() {
    document.querySelectorAll('.block-card').forEach((block) => {
      block.addEventListener('dragstart', this.handleBlockDragStart.bind(this))
      block.addEventListener('dragend', this.handleDragEnd.bind(this))
    })
  }

  handleTemplateDragStart(event) {
    this.dragType = 'template'
    this.dragData = {
      templateId: event.currentTarget.dataset.templateId,
      blockType: event.currentTarget.dataset.blockType,
      templateName: event.currentTarget.dataset.templateName,
      defaultContent: JSON.parse(event.currentTarget.dataset.defaultContent || '{}'),
      defaultSettings: JSON.parse(event.currentTarget.dataset.defaultSettings || '{}')
    }
    event.currentTarget.classList.add('opacity-50')
  }

  handleBlockDragStart(event) {
    this.dragType = 'block'
    this.dragData = {
      blockId: event.currentTarget.dataset.blockId
    }
    event.currentTarget.classList.add('opacity-50')
    event.dataTransfer.effectAllowed = 'move'
  }

  handleDragOver(event) {
    event.preventDefault()
    event.dataTransfer.dropEffect = this.dragType === 'template' ? 'copy' : 'move'

    // Add visual feedback
    const container = this.blocksContainerTarget
    container.classList.add('bg-blue-50', 'border-blue-300')

    // Find drop position
    const afterElement = this.getDragAfterElement(container, event.clientY)
    const placeholder = document.getElementById('drop-placeholder')

    if (!placeholder) {
      const div = document.createElement('div')
      div.id = 'drop-placeholder'
      div.className = 'h-2 bg-blue-400 rounded-full my-2 transition-all'
      container.appendChild(div)
    }

    if (afterElement) {
      container.insertBefore(document.getElementById('drop-placeholder'), afterElement)
    } else {
      container.appendChild(document.getElementById('drop-placeholder'))
    }
  }

  handleDragLeave(event) {
    // Only remove if leaving the container entirely
    if (!this.blocksContainerTarget.contains(event.relatedTarget)) {
      this.blocksContainerTarget.classList.remove('bg-blue-50', 'border-blue-300')
      document.getElementById('drop-placeholder')?.remove()
    }
  }

  handleDragEnd(event) {
    event.currentTarget.classList.remove('opacity-50')
    this.blocksContainerTarget.classList.remove('bg-blue-50', 'border-blue-300')
    document.getElementById('drop-placeholder')?.remove()
  }

  async handleDrop(event) {
    event.preventDefault()
    this.blocksContainerTarget.classList.remove('bg-blue-50', 'border-blue-300')

    const placeholder = document.getElementById('drop-placeholder')
    const position = this.getDropPosition(placeholder)
    placeholder?.remove()

    // Hide empty state
    document.getElementById('empty-state')?.remove()

    if (this.dragType === 'template') {
      await this.addBlock(this.dragData, position)
    } else if (this.dragType === 'block') {
      await this.reorderBlock(this.dragData.blockId, position)
    }

    this.dragType = null
    this.dragData = null
  }

  getDragAfterElement(container, y) {
    const draggableElements = [...container.querySelectorAll('.block-card:not(.opacity-50)')]

    return draggableElements.reduce((closest, child) => {
      const box = child.getBoundingClientRect()
      const offset = y - box.top - box.height / 2

      if (offset < 0 && offset > closest.offset) {
        return { offset: offset, element: child }
      }
      return closest
    }, { offset: Number.NEGATIVE_INFINITY }).element
  }

  getDropPosition(placeholder) {
    if (!placeholder) return 0

    const blocks = [...this.blocksContainerTarget.querySelectorAll('.block-card')]
    let position = 0

    for (let i = 0; i < blocks.length; i++) {
      if (placeholder.compareDocumentPosition(blocks[i]) & Node.DOCUMENT_POSITION_FOLLOWING) {
        break
      }
      position++
    }

    return position
  }

  // ==========================================
  // Block CRUD Operations
  // ==========================================

  async addBlock(templateData, position) {
    const response = await fetch(`/admin/websites/${this.websiteIdValue}/pages/${this.pageIdValue}/blocks`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': this.csrfValue
      },
      body: JSON.stringify({
        template_id: templateData.templateId,
        block_type: templateData.blockType,
        position: position,
        content: templateData.defaultContent,
        settings: templateData.defaultSettings
      })
    })

    if (response.ok) {
      const data = await response.json()
      this.insertBlockCard(data.block, position)
      this.showNotification('Block added', 'success')
    } else {
      this.showNotification('Failed to add block', 'error')
    }
  }

  async reorderBlock(blockId, newPosition) {
    const blockIds = [...this.blocksContainerTarget.querySelectorAll('.block-card')]
      .map((el) => el.dataset.blockId)
      .filter((id) => id !== blockId)

    blockIds.splice(newPosition, 0, blockId)

    const response = await fetch(`/admin/websites/${this.websiteIdValue}/pages/${this.pageIdValue}/blocks/reorder`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': this.csrfValue
      },
      body: JSON.stringify({ block_ids: blockIds })
    })

    if (response.ok) {
      // Reorder DOM elements
      const block = document.querySelector(`.block-card[data-block-id="${blockId}"]`)
      const blocks = [...this.blocksContainerTarget.querySelectorAll('.block-card')]
      const targetBlock = blocks[newPosition]

      if (targetBlock && targetBlock !== block) {
        this.blocksContainerTarget.insertBefore(block, targetBlock)
      } else {
        this.blocksContainerTarget.appendChild(block)
      }
    }
  }

  async deleteBlock(event) {
    const blockId = event.currentTarget.dataset.blockId
    if (!confirm('Are you sure you want to delete this block?')) return

    const response = await fetch(`/admin/websites/${this.websiteIdValue}/pages/${this.pageIdValue}/blocks/${blockId}`, {
      method: 'DELETE',
      headers: {
        'X-CSRF-Token': this.csrfValue
      }
    })

    if (response.ok) {
      document.querySelector(`.block-card[data-block-id="${blockId}"]`)?.remove()
      this.showNotification('Block deleted', 'success')

      // Show empty state if no blocks left
      if (this.blocksContainerTarget.querySelectorAll('.block-card').length === 0) {
        this.showEmptyState()
      }
    }
  }

  async toggleVisibility(event) {
    const blockId = event.currentTarget.dataset.blockId
    const blockCard = document.querySelector(`.block-card[data-block-id="${blockId}"]`)
    const isCurrentlyVisible = !blockCard.classList.contains('opacity-50')

    const response = await fetch(`/admin/websites/${this.websiteIdValue}/pages/${this.pageIdValue}/blocks/${blockId}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': this.csrfValue
      },
      body: JSON.stringify({ block: { visible: !isCurrentlyVisible } })
    })

    if (response.ok) {
      blockCard.classList.toggle('opacity-50')
    }
  }

  async duplicateBlock(event) {
    const blockId = event.currentTarget.dataset.blockId
    const blockCard = document.querySelector(`.block-card[data-block-id="${blockId}"]`)
    const blockType = blockCard.dataset.blockType

    // Get current block's position
    const blocks = [...this.blocksContainerTarget.querySelectorAll('.block-card')]
    const currentPosition = blocks.indexOf(blockCard)

    const response = await fetch(`/admin/websites/${this.websiteIdValue}/pages/${this.pageIdValue}/blocks`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': this.csrfValue
      },
      body: JSON.stringify({
        block_type: blockType,
        position: currentPosition + 1
      })
    })

    if (response.ok) {
      const data = await response.json()
      this.insertBlockCard(data.block, currentPosition + 1)
      this.showNotification('Block duplicated', 'success')
    }
  }

  // ==========================================
  // Block Editing
  // ==========================================

  editBlock(event) {
    const blockId = event.currentTarget.dataset.blockId
    this.editingBlockId = blockId

    // Highlight the selected block
    document.querySelectorAll('.block-card').forEach((card) => {
      card.classList.remove('ring-2', 'ring-blue-500')
    })
    document.querySelector(`.block-card[data-block-id="${blockId}"]`)?.classList.add('ring-2', 'ring-blue-500')

    // Show editor sidebar
    this.editorSidebarTarget.classList.remove('hidden')

    // Load block editor form
    this.loadBlockEditor(blockId)
  }

  async loadBlockEditor(blockId) {
    const block = this.blocksValue.find((b) => b.id === blockId)
    const blockType = block?.block_type || 'unknown'
    const isSectionBlock = this.isSectionBlock(blockType)

    // Escape values for HTML
    const escapeHtml = (str) => {
      if (!str) return ''
      return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
    }

    // Get current surface value
    const currentSurface = block?.settings?.surface || block?.content?.surface || 'light'

    // Build tabbed editor HTML
    // Safely encode block data to avoid JSON parsing issues with special characters
    const safeBlockJson = btoa(encodeURIComponent(JSON.stringify(block || {})))

    this.editorContentTarget.innerHTML = `
      <div data-controller="admin--block-editor"
           data-admin--block-editor-website-id-value="${this.websiteIdValue}"
           data-admin--block-editor-page-id-value="${this.pageIdValue}"
           data-admin--block-editor-block-id-value="${blockId}"
           data-admin--block-editor-block-type-value="${blockType}"
           data-admin--block-editor-block-json-value="${safeBlockJson}"
           data-admin--block-editor-csrf-value="${this.csrfValue}"
           data-action="admin--block-editor:blockUpdated->page-editor#handleBlockEditorUpdate admin--block-editor:backgroundUpdated->page-editor#handleBackgroundUpdate">

        <!-- Tab Navigation -->
        <div class="flex border-b border-gray-200 mb-4 -mx-4 px-4">
          <button type="button"
                  data-admin--block-editor-target="tabButton"
                  data-tab="content"
                  data-action="click->admin--block-editor#switchTab"
                  class="flex-1 px-3 py-2.5 text-sm font-medium border-b-2 transition-colors border-blue-500 text-blue-600 bg-blue-50">
            <span class="inline-flex items-center justify-center gap-1.5">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
              </svg>
              Content
            </span>
          </button>
          ${isSectionBlock ? `
          <button type="button"
                  data-admin--block-editor-target="tabButton"
                  data-tab="style"
                  data-action="click->admin--block-editor#switchTab"
                  class="flex-1 px-3 py-2.5 text-sm font-medium border-b-2 transition-colors border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50">
            <span class="inline-flex items-center justify-center gap-1.5">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01"/>
              </svg>
              Style
            </span>
          </button>
          <button type="button"
                  data-admin--block-editor-target="tabButton"
                  data-tab="background"
                  data-action="click->admin--block-editor#switchTab"
                  class="flex-1 px-3 py-2.5 text-sm font-medium border-b-2 transition-colors border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50">
            <span class="inline-flex items-center justify-center gap-1.5">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
              </svg>
              Background
            </span>
          </button>
          ` : ''}
        </div>

        <!-- Content Panel -->
        <div data-admin--block-editor-target="contentPanel">
          <form id="block-edit-form" class="space-y-4" data-action="submit->admin--block-editor#saveContent">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Section Title</label>
              <input type="text" name="section_title" value="${escapeHtml(block?.section_title)}"
                     class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                     placeholder="Optional section title">
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Heading</label>
              <input type="text" name="heading" value="${escapeHtml(block?.content?.heading)}"
                     class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Subheading</label>
              <input type="text" name="subheading" value="${escapeHtml(block?.content?.subheading)}"
                     class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Content</label>
              <textarea name="body_text" rows="4"
                        class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500">${escapeHtml(block?.content?.body_text)}</textarea>
            </div>

            <div class="pt-4 border-t border-gray-200">
              <button type="submit" class="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium">
                Update Block
              </button>
            </div>
          </form>
        </div>

        ${isSectionBlock ? `
        <!-- Style Panel -->
        <div data-admin--block-editor-target="stylePanel" class="hidden">
          <div class="space-y-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">Surface Style</label>
              <p class="text-xs text-gray-500 mb-3">Choose a background surface for this section</p>
              <div class="grid grid-cols-2 gap-2">
                <label class="relative cursor-pointer">
                  <input type="radio" name="surface" value="light" ${currentSurface === 'light' ? 'checked' : ''}
                         class="sr-only peer" data-action="change->admin--block-editor#saveSurface">
                  <div class="p-3 rounded-lg border-2 transition-all peer-checked:border-blue-500 peer-checked:bg-blue-50 border-gray-200 hover:border-gray-300">
                    <div class="w-full h-8 rounded bg-white border border-gray-200 mb-2"></div>
                    <span class="text-sm font-medium text-gray-700">Light</span>
                  </div>
                </label>
                <label class="relative cursor-pointer">
                  <input type="radio" name="surface" value="muted" ${currentSurface === 'muted' ? 'checked' : ''}
                         class="sr-only peer" data-action="change->admin--block-editor#saveSurface">
                  <div class="p-3 rounded-lg border-2 transition-all peer-checked:border-blue-500 peer-checked:bg-blue-50 border-gray-200 hover:border-gray-300">
                    <div class="w-full h-8 rounded bg-gray-100 mb-2"></div>
                    <span class="text-sm font-medium text-gray-700">Muted</span>
                  </div>
                </label>
                <label class="relative cursor-pointer">
                  <input type="radio" name="surface" value="primary" ${currentSurface === 'primary' ? 'checked' : ''}
                         class="sr-only peer" data-action="change->admin--block-editor#saveSurface">
                  <div class="p-3 rounded-lg border-2 transition-all peer-checked:border-blue-500 peer-checked:bg-blue-50 border-gray-200 hover:border-gray-300">
                    <div class="w-full h-8 rounded bg-blue-600 mb-2"></div>
                    <span class="text-sm font-medium text-gray-700">Primary</span>
                  </div>
                </label>
                <label class="relative cursor-pointer">
                  <input type="radio" name="surface" value="inverse" ${currentSurface === 'inverse' ? 'checked' : ''}
                         class="sr-only peer" data-action="change->admin--block-editor#saveSurface">
                  <div class="p-3 rounded-lg border-2 transition-all peer-checked:border-blue-500 peer-checked:bg-blue-50 border-gray-200 hover:border-gray-300">
                    <div class="w-full h-8 rounded bg-gray-900 mb-2"></div>
                    <span class="text-sm font-medium text-gray-700">Inverse</span>
                  </div>
                </label>
              </div>
            </div>
          </div>
        </div>

        <!-- Background Panel -->
        <div data-admin--block-editor-target="backgroundPanel" class="hidden">
          <div data-admin--block-editor-target="backgroundMount">
            <!-- Vue BlockBackgroundEditor component will be mounted here -->
            <div class="text-center py-8 text-gray-500">
              <svg class="w-8 h-8 mx-auto mb-2 animate-spin text-blue-600" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <p class="text-sm">Loading background editor...</p>
            </div>
          </div>
        </div>
        ` : ''}
      </div>
    `
  }

  /**
   * Check if block type is a section block that supports backgrounds
   * Most blocks support backgrounds except for header/footer/navigation blocks
   */
  isSectionBlock(blockType) {
    // Blocks that do NOT support custom backgrounds
    const nonSectionBlocks = [
      'header', 'footer', 'navigation', 'nav',
      'breadcrumbs', 'sidebar'
    ]
    // If it's not in the exclusion list, it's a section block
    return blockType && !nonSectionBlocks.includes(blockType)
  }

  async saveBlockChanges(form) {
    const formData = new FormData(form)
    const content = {
      heading: formData.get('heading'),
      subheading: formData.get('subheading'),
      body_text: formData.get('body_text')
    }

    const response = await fetch(`/admin/websites/${this.websiteIdValue}/pages/${this.pageIdValue}/blocks/${this.editingBlockId}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': this.csrfValue
      },
      body: JSON.stringify({
        block: {
          section_title: formData.get('section_title'),
          content: content
        }
      })
    })

    if (response.ok) {
      this.showNotification('Block updated', 'success')
      // Update the block card preview
      const data = await response.json()
      this.updateBlockCard(data.block)
    }
  }

  closeEditor() {
    this.editorSidebarTarget.classList.add('hidden')
    document.querySelectorAll('.block-card').forEach((card) => {
      card.classList.remove('ring-2', 'ring-blue-500')
    })
    this.editingBlockId = null
  }

  // ==========================================
  // Block Picker Modal
  // ==========================================

  openBlockPicker() {
    this.blockPickerModalTarget.classList.remove('hidden')
  }

  closeBlockPicker() {
    this.blockPickerModalTarget.classList.add('hidden')
    this.insertContext = null // Clear insert context when closing
    // Reset modal title if it was changed for insert mode
    const title = this.blockPickerModalTarget.querySelector('h2, h3')
    if (title) {
      title.textContent = 'Add a Block'
    }
  }

  // Open block picker in insert mode (before/after a specific block)
  openInsertPicker(event) {
    const blockId = event.currentTarget.dataset.blockId
    const direction = event.currentTarget.dataset.direction // 'before' or 'after'

    logger.log('Opening insert picker', { blockId, direction })

    // Store the insert context
    this.insertContext = { blockId, direction }

    // Update modal title to reflect insert mode
    const title = this.blockPickerModalTarget.querySelector('h2, h3')
    if (title) {
      title.textContent = direction === 'before' ? 'Insert Block Before' : 'Insert Block After'
    }

    this.openBlockPicker()
  }

  async addBlockFromPicker(event) {
    const templateId = event.currentTarget.dataset.templateId
    const blockType = event.currentTarget.dataset.blockType

    // Check if we're in insert mode
    if (this.insertContext) {
      const { blockId, direction } = this.insertContext
      await this.insertBlock({ templateId, blockType, defaultContent: {}, defaultSettings: {} }, blockId, direction)
    } else {
      const position = this.blocksContainerTarget.querySelectorAll('.block-card').length
      await this.addBlock({ templateId, blockType, defaultContent: {}, defaultSettings: {} }, position)
    }
    this.closeBlockPicker()
  }

  // Insert a block before or after an existing block
  async insertBlock(templateData, referenceBlockId, direction) {
    const body = {
      template_id: templateData.templateId,
      block_type: templateData.blockType,
      content: templateData.defaultContent,
      settings: templateData.defaultSettings
    }

    // Set the appropriate insert param
    if (direction === 'before') {
      body.insert_before_block_id = referenceBlockId
    } else {
      body.insert_after_block_id = referenceBlockId
    }

    const response = await fetch(`/admin/websites/${this.websiteIdValue}/pages/${this.pageIdValue}/blocks`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': this.csrfValue
      },
      body: JSON.stringify(body)
    })

    if (response.ok) {
      const _data = await response.json()
      // Reload page to ensure correct ordering (simpler than DOM manipulation)
      this.showNotification('Block inserted', 'success')
      window.location.reload()
    } else {
      this.showNotification('Failed to insert block', 'error')
    }
  }

  // ==========================================
  // UI Helpers
  // ==========================================

  insertBlockCard(blockData, position) {
    const card = document.createElement('div')
    card.className = 'block-card bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden group'
    card.dataset.blockId = blockData.id
    card.dataset.blockType = blockData.block_type
    card.draggable = true

    card.innerHTML = `
      <div class="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between cursor-grab">
        <div class="flex items-center gap-3">
          <div class="text-gray-400 hover:text-gray-600">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 8h16M4 16h16"/>
            </svg>
          </div>
          <div class="w-8 h-8 rounded bg-blue-100 flex items-center justify-center">
            <svg class="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6z"/>
            </svg>
          </div>
          <div>
            <p class="font-medium text-sm text-gray-900">${blockData.section_title || blockData.block_type.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase())}</p>
            <p class="text-xs text-gray-500">${blockData.block_type}</p>
          </div>
        </div>
        <div class="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition">
          <button type="button" class="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition" data-action="click->page-editor#editBlock" data-block-id="${blockData.id}" title="Edit">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/></svg>
          </button>
          <button type="button" class="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition" data-action="click->page-editor#deleteBlock" data-block-id="${blockData.id}" title="Delete">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/></svg>
          </button>
        </div>
      </div>
      <div class="p-4 cursor-pointer" data-action="click->page-editor#editBlock" data-block-id="${blockData.id}">
        <div class="text-sm text-gray-400 italic">Click to edit this block</div>
      </div>
    `

    const blocks = [...this.blocksContainerTarget.querySelectorAll('.block-card')]
    if (blocks[position]) {
      this.blocksContainerTarget.insertBefore(card, blocks[position])
    } else {
      this.blocksContainerTarget.appendChild(card)
    }

    // Re-setup drag events
    card.addEventListener('dragstart', this.handleBlockDragStart.bind(this))
    card.addEventListener('dragend', this.handleDragEnd.bind(this))

    // Update blocks value
    this.blocksValue = [...this.blocksValue, blockData]
  }

  updateBlockCard(blockData) {
    const card = document.querySelector(`.block-card[data-block-id="${blockData.id}"]`)
    if (!card) return

    const titleEl = card.querySelector('.font-medium.text-sm')
    if (titleEl) {
      titleEl.textContent = blockData.section_title || blockData.content?.heading || blockData.block_type.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase())
    }

    // Update blocks value
    this.blocksValue = this.blocksValue.map((b) => (b.id === blockData.id ? blockData : b))
  }

  showEmptyState() {
    this.blocksContainerTarget.innerHTML = `
      <div id="empty-state" class="border-2 border-dashed border-gray-300 rounded-xl p-12 text-center">
        <svg class="w-12 h-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"/>
        </svg>
        <h3 class="text-lg font-medium text-gray-900 mb-1">No blocks yet</h3>
        <p class="text-gray-500">Drag blocks from the library to start building your page</p>
      </div>
    `
  }

  showNotification(message, type = 'success') {
    const colorClasses = {
      success: 'bg-green-600',
      error: 'bg-red-600',
      info: 'bg-purple-600'
    }
    const notification = document.createElement('div')
    notification.className = `fixed bottom-4 right-4 px-4 py-2 rounded-lg text-white ${colorClasses[type] || colorClasses.success} shadow-lg z-50 transition-opacity duration-300`
    notification.textContent = message
    document.body.appendChild(notification)

    setTimeout(() => {
      notification.classList.add('opacity-0')
      setTimeout(() => notification.remove(), 300)
    }, 2000)
  }

  // ==========================================
  // AI Block Regeneration / Editing
  // ==========================================

  openAiModal(event) {
    this.aiTargetBlockId = event.currentTarget.dataset.blockId
    const blockType = event.currentTarget.dataset.blockType

    // Update modal with block type info
    const blockTypeEl = document.getElementById('ai-modal-block-type')
    if (blockTypeEl) {
      blockTypeEl.textContent = blockType.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())
    }

    // Clear previous prompt
    if (this.hasAiPromptInputTarget) {
      this.aiPromptInputTarget.value = ''
    }

    // Default to edit mode
    this.aiMode = 'edit'
    this.updateAiModalForMode()

    // Show modal
    this.aiModalTarget.classList.remove('hidden')
  }

  closeAiModal() {
    this.aiModalTarget.classList.add('hidden')
    this.aiTargetBlockId = null
  }

  switchAiMode(event) {
    const mode = event.currentTarget.dataset.mode
    if (mode === this.aiMode) return

    this.aiMode = mode
    this.updateAiModalForMode()
  }

  updateAiModalForMode() {
    const editTab = document.getElementById('ai-tab-edit')
    const contentTab = document.getElementById('ai-tab-content')
    const generateTab = document.getElementById('ai-tab-generate')
    const modalTitle = document.getElementById('ai-modal-title')
    const promptLabel = document.getElementById('ai-prompt-label')
    const promptInput = document.getElementById('ai-prompt-input')
    const helpText = document.getElementById('ai-help-text')
    const btnIcon = document.getElementById('ai-btn-icon')
    const btnText = document.getElementById('ai-btn-text')

    // Tab styling classes
    const activeClass = 'flex-1 px-3 py-3 text-sm font-medium text-purple-600 border-b-2 border-purple-600 bg-purple-50'
    const inactiveClass = 'flex-1 px-3 py-3 text-sm font-medium text-gray-500 hover:text-gray-700 border-b-2 border-transparent hover:bg-gray-50'

    // Reset all tabs to inactive
    editTab.className = inactiveClass
    contentTab.className = inactiveClass
    generateTab.className = inactiveClass

    // Mode configurations
    const modeConfig = {
      edit: {
        tab: editTab,
        title: 'AI Edit Block',
        label: 'Describe the changes you want',
        placeholder: 'e.g., Make the headline more urgent, change the button color to red, add a third statistic...',
        help: 'The AI will modify your existing block based on these instructions.',
        btnText: 'Apply Changes',
        btnIcon: '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>'
      },
      content: {
        tab: contentTab,
        title: 'AI Update Content',
        label: 'Describe how to update the text content',
        placeholder: 'e.g., Make the copy more professional, update the headline to mention 24/7 service, change stats to show 500+ customers...',
        help: 'The AI will update the text content while preserving the block\'s design and layout.',
        btnText: 'Update Content',
        btnIcon: '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>'
      },
      generate: {
        tab: generateTab,
        title: 'AI Generate Block',
        label: 'Describe the block you want',
        placeholder: 'e.g., A hero with a bold headline about our 24/7 emergency services, include customer review stars, and a bright red CTA button...',
        help: 'The AI will generate a completely new block replacing the current one.',
        btnText: 'Generate',
        btnIcon: '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"/>'
      }
    }

    const config = modeConfig[this.aiMode] || modeConfig.edit

    // Activate the selected tab
    config.tab.className = activeClass

    // Update modal content
    modalTitle.textContent = config.title
    promptLabel.textContent = config.label
    promptInput.placeholder = config.placeholder
    helpText.textContent = config.help
    btnText.textContent = config.btnText
    btnIcon.innerHTML = config.btnIcon
  }

  async submitAiAction() {
    logger.log('submitAiAction called, aiMode:', this.aiMode, 'blockId:', this.aiTargetBlockId)
    switch (this.aiMode) {
      case 'edit':
        await this.editBlockWithAi()
        break
      case 'content':
        await this.regenerateContentWithAi()
        break
      case 'generate':
        await this.generateBlockContent()
        break
      default:
        await this.editBlockWithAi()
    }
  }

  async editBlockWithAi() {
    logger.log('editBlockWithAi called - NEW CODE v2')
    if (!this.aiTargetBlockId) return

    const prompt = this.aiPromptInputTarget.value.trim()
    if (!prompt) {
      this.showNotification('Please describe the changes you want', 'error')
      return
    }

    const blockId = this.aiTargetBlockId
    logger.log('Starting edit processing for:', blockId)

    // Start animation and close modal immediately for better UX
    this.startBlockProcessing(blockId, 'edit')
    this.closeAiModal()
    this.showNotification('AI is editing your block...', 'info')

    try {
      const response = await fetch(this.editUrlValue, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.csrfValue
        },
        body: JSON.stringify({
          block_id: blockId,
          prompt: prompt
        })
      })

      const data = await response.json()

      if (!response.ok || !data.success) {
        // Immediate error (validation, not found, etc.)
        this.endBlockProcessingWithError(blockId, data.error || 'Failed to start editing')
      }
      // If successful, job is queued - ActionCable will handle the result
    } catch (error) {
      console.error('AI edit error:', error)
      this.endBlockProcessingWithError(blockId, 'Failed to start editing')
    }
  }

  async regenerateContentWithAi() {
    logger.log('regenerateContentWithAi called - NEW CODE v2')
    if (!this.aiTargetBlockId) return

    const prompt = this.aiPromptInputTarget.value.trim()
    if (!prompt) {
      this.showNotification('Please describe how to update the content', 'error')
      return
    }

    const blockId = this.aiTargetBlockId
    logger.log('Starting content processing for:', blockId)

    // Start animation and close modal immediately for better UX
    this.startBlockProcessing(blockId, 'content')
    this.closeAiModal()
    this.showNotification('AI is updating your content...', 'info')

    try {
      const response = await fetch(this.contentUrlValue, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.csrfValue
        },
        body: JSON.stringify({
          block_id: blockId,
          prompt: prompt
        })
      })

      const data = await response.json()

      if (!response.ok || !data.success) {
        // Immediate error (validation, not found, etc.)
        this.endBlockProcessingWithError(blockId, data.error || 'Failed to start content update')
      }
      // If successful, job is queued - ActionCable will handle the result
    } catch (error) {
      console.error('AI content update error:', error)
      this.endBlockProcessingWithError(blockId, 'Failed to start content update')
    }
  }

  async generateBlockContent() {
    logger.log('generateBlockContent called - NEW CODE v2')
    if (!this.aiTargetBlockId) return

    const prompt = this.aiPromptInputTarget.value.trim()
    if (!prompt) {
      this.showNotification('Please enter a description', 'error')
      return
    }

    const blockId = this.aiTargetBlockId
    logger.log('Starting block processing for:', blockId)

    // Start animation and close modal immediately for better UX
    this.startBlockProcessing(blockId, 'generate')
    this.closeAiModal()
    this.showNotification('AI is generating new content...', 'info')

    try {
      const response = await fetch(this.regenerateUrlValue, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.csrfValue
        },
        body: JSON.stringify({
          block_id: blockId,
          prompt: prompt
        })
      })

      const data = await response.json()

      if (!response.ok || !data.success) {
        // Immediate error (validation, not found, etc.)
        this.endBlockProcessingWithError(blockId, data.error || 'Failed to start generation')
      }
      // If successful, job is queued - ActionCable will handle the result
    } catch (error) {
      console.error('AI generation error:', error)
      this.endBlockProcessingWithError(blockId, 'Failed to start generation')
    }
  }

  // ==========================================
  // Undo / Rollback
  // ==========================================

  updateUndoButton() {
    const undoBtn = document.getElementById('undo-btn')
    if (!undoBtn) return

    if (this.lastChangedBlock) {
      undoBtn.classList.remove('hidden')
      undoBtn.disabled = false
    } else {
      undoBtn.disabled = true
    }
  }

  async undoLastChange() {
    if (!this.lastChangedBlock) return

    const { blockId, previousVersion } = this.lastChangedBlock
    const undoBtn = document.getElementById('undo-btn')

    // Show loading state
    undoBtn.disabled = true
    undoBtn.innerHTML = `
      <svg class="w-4 h-4 animate-spin inline mr-1" fill="none" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
      </svg>
      Undoing...
    `

    try {
      const response = await fetch(this.rollbackUrlValue, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.csrfValue
        },
        body: JSON.stringify({
          block_id: blockId,
          version: previousVersion
        })
      })

      const data = await response.json()

      if (response.ok && data.success) {
        // Update block in our local state
        this.blocksValue = this.blocksValue.map((b) => {
          if (b.id === blockId) {
            return { ...b, content: data.content, content_version: data.version }
          }
          return b
        })

        // Update block card display
        this.updateBlockCard({ id: blockId, content: data.content })

        // Clear undo state
        this.lastChangedBlock = null
        this.showNotification('Change undone', 'success')
      } else {
        this.showNotification(data.error || 'Failed to undo', 'error')
      }
    } catch (error) {
      console.error('Undo error:', error)
      this.showNotification('Failed to undo', 'error')
    } finally {
      // Restore button
      undoBtn.innerHTML = `
        <svg class="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6"/>
        </svg>
        Undo
      `
      this.updateUndoButton()
    }
  }

  // ==========================================
  // Version History Integration
  // ==========================================

  /**
   * Handle version restored event from block-history controller
   * Updates local state, UI, and undo tracking
   */
  handleVersionRestored(event) {
    const { blockId, content, renderedHtml: _renderedHtml, version } = event.detail

    // Update block in local state
    this.blocksValue = this.blocksValue.map((b) => {
      if (b.id === blockId) {
        return {
          ...b,
          content: content,
          content_version: version,
          has_history: true
        }
      }
      return b
    })

    // Update the block card display
    this.updateBlockCard({ id: blockId, content: content })

    // Track for potential undo (restoring creates a new version)
    this.lastChangedBlock = {
      blockId: blockId,
      previousVersion: version - 1
    }
    this.updateUndoButton()

    // Show success notification
    this.showNotification(`Restored to version ${version - 1}`, 'success')
  }

  // ==========================================
  // Block Editor Integration
  // ==========================================

  /**
   * Handle block update event from block-editor controller
   * Updates local state and block card display
   */
  handleBlockEditorUpdate(event) {
    const block = event.detail

    // Update block in local state
    this.blocksValue = this.blocksValue.map((b) => {
      if (b.id === block.id) {
        return { ...b, ...block }
      }
      return b
    })

    // Update block card display
    this.updateBlockCard(block)
  }

  /**
   * Handle background update event from block-editor controller
   * Updates local state with new background settings
   */
  handleBackgroundUpdate(event) {
    const { blockId, background, renderedHtml } = event.detail

    // Update block in local state
    this.blocksValue = this.blocksValue.map((b) => {
      if (b.id === blockId) {
        const settings = b.settings || {}
        settings.background = background
        return { ...b, settings }
      }
      return b
    })

    // Update block card to show background indicator
    this.updateBlockCardBackground(blockId, background)

    // Refresh the preview iframe with the new rendered HTML
    if (renderedHtml) {
      this.refreshPreview(blockId, renderedHtml)
    }
  }

  /**
   * Refresh the preview iframe with updated block HTML
   */
  refreshPreview(blockId, renderedHtml) {
    // Find the preview iframe
    const previewIframe = document.querySelector('#previewFrame, iframe[name="preview"]')
    if (!previewIframe || !previewIframe.contentDocument) return

    // Find the block wrapper in the preview (the div with data-block-id and data-block-type attributes)
    const blockSelector = `[data-block-id="${blockId}"][data-block-type]`
    const blockWrapperInPreview = previewIframe.contentDocument.querySelector(blockSelector)

    if (blockWrapperInPreview && renderedHtml) {
      // The rendered_html is the section content inside the wrapper
      // We need to find the inner section element with data-block-id
      const innerBlockSelector = `section[data-block-id="${blockId}"]`
      const innerBlock = blockWrapperInPreview.querySelector(innerBlockSelector) || blockWrapperInPreview

      // Create a temporary container to parse the new HTML
      const temp = document.createElement('div')
      temp.innerHTML = renderedHtml

      // Get the new section element
      const newBlock = temp.querySelector('section') || temp.firstElementChild
      if (newBlock && innerBlock) {
        innerBlock.replaceWith(newBlock)
      }
    }
  }

  /**
   * Update block card to show background indicator
   */
  updateBlockCardBackground(blockId, background) {
    const card = document.querySelector(`.block-card[data-block-id="${blockId}"]`)
    if (!card) return

    // Find or create background indicator
    let indicator = card.querySelector('.background-indicator')
    const hasBackground = background && background.type && background.type !== 'none'

    if (hasBackground) {
      if (!indicator) {
        indicator = document.createElement('span')
        indicator.className = 'background-indicator inline-flex items-center gap-1 text-xs text-purple-600 bg-purple-50 px-2 py-0.5 rounded-full ml-2'
        indicator.innerHTML = `
          <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/>
          </svg>
          <span>${background.type}</span>
        `
        const titleEl = card.querySelector('.font-medium.text-sm')
        if (titleEl && titleEl.parentNode) {
          titleEl.parentNode.appendChild(indicator)
        }
      } else {
        // Update existing indicator
        const typeSpan = indicator.querySelector('span')
        if (typeSpan) {
          typeSpan.textContent = background.type
        }
      }
    } else if (indicator) {
      // Remove indicator if no background
      indicator.remove()
    }
  }

  // ==========================================
  // AI Processing Animations
  // ==========================================

  /**
   * Setup observer to re-apply animations when iframe reloads
   */
  setupIframeObserver() {
    const iframe = document.querySelector('#previewFrame, iframe[name="preview"]')
    if (!iframe) return

    iframe.addEventListener('load', () => {
      this.iframeStylesInjected = false
      // Re-apply animations to any blocks still processing
      this.processingBlocks.forEach((state, blockId) => {
        this.applyProcessingAnimation(blockId, state.mode)
      })
    })
  }

  /**
   * Monitor for stale processing operations (timeout after 35 seconds)
   */
  startTimeoutMonitor() {
    this.timeoutInterval = setInterval(() => {
      const now = Date.now()
      const timeout = 35000 // 35 seconds

      this.processingBlocks.forEach((state, blockId) => {
        if (now - state.startTime > timeout) {
          logger.warn(`Block ${blockId} processing timeout`)
          this.endBlockProcessingWithError(blockId, 'Operation timed out. Please try again.')
        }
      })
    }, 5000)
  }

  /**
   * Start processing animation for a block
   */
  startBlockProcessing(blockId, mode) {
    this.processingBlocks.set(blockId, {
      mode,
      startTime: Date.now()
    })
    this.applyProcessingAnimation(blockId, mode)
    this.updateBlockCardProcessingState(blockId, true, mode)
  }

  /**
   * End processing and show success animation
   */
  endBlockProcessingWithSuccess(blockId) {
    const iframe = document.querySelector('#previewFrame, iframe[name="preview"]')
    const block = iframe?.contentDocument?.querySelector(`[data-block-id="${blockId}"]`)

    if (block) {
      // Remove processing classes
      block.classList.remove('ai-processing-edit', 'ai-processing-content', 'ai-processing-generate')

      // Remove skeleton overlay if present
      const skeleton = block.querySelector('.ai-skeleton-overlay')
      skeleton?.remove()

      // Remove status badge
      const badge = block.querySelector('.ai-status-badge')
      badge?.remove()

      // Add success flash
      block.classList.add('ai-processing-success')
      setTimeout(() => block.classList.remove('ai-processing-success'), 600)
    }

    // Update sidebar card
    this.updateBlockCardProcessingState(blockId, false)
    this.processingBlocks.delete(blockId)
  }

  /**
   * End processing with error state
   */
  endBlockProcessingWithError(blockId, errorMessage) {
    const iframe = document.querySelector('#previewFrame, iframe[name="preview"]')
    const block = iframe?.contentDocument?.querySelector(`[data-block-id="${blockId}"]`)

    if (block) {
      // Remove processing classes
      block.classList.remove('ai-processing-edit', 'ai-processing-content', 'ai-processing-generate')

      // Remove skeleton overlay if present
      const skeleton = block.querySelector('.ai-skeleton-overlay')
      skeleton?.remove()

      // Add error animation briefly
      block.classList.add('ai-processing-error')
      setTimeout(() => block.classList.remove('ai-processing-error'), 500)

      // Update badge to show error
      const badge = block.querySelector('.ai-status-badge')
      if (badge) {
        badge.classList.add('ai-status-error')
        badge.querySelector('span').textContent = 'Failed'
        setTimeout(() => badge.remove(), 2000)
      }
    }

    // Update sidebar card with error flash
    this.updateBlockCardProcessingState(blockId, false)
    const card = document.querySelector(`.block-card[data-block-id="${blockId}"]`)
    if (card) {
      card.classList.add('ring-2', 'ring-red-400')
      setTimeout(() => card.classList.remove('ring-2', 'ring-red-400'), 3000)
    }

    // Show error notification
    this.showNotification(errorMessage || 'AI operation failed', 'error')

    // Clean up state
    this.processingBlocks.delete(blockId)
  }

  /**
   * Inject animation styles into the preview iframe
   */
  ensureIframeStyles() {
    const iframe = document.querySelector('#previewFrame, iframe[name="preview"]')
    if (!iframe?.contentDocument || this.iframeStylesInjected) return

    // Get the primary color from iframe's CSS variables or use fallback
    const iframeRoot = iframe.contentDocument.documentElement
    const computedStyle = iframe.contentWindow.getComputedStyle(iframeRoot)
    const primaryColor = computedStyle.getPropertyValue('--color-primary').trim() || '#7c3aed'
    const primaryRgb = this.hexToRgb(primaryColor)

    const styleEl = iframe.contentDocument.createElement('style')
    styleEl.id = 'ai-processing-styles'
    styleEl.textContent = this.getAnimationStyles(primaryRgb)
    iframe.contentDocument.head.appendChild(styleEl)
    this.iframeStylesInjected = true
  }

  /**
   * Convert hex color to RGB values
   */
  hexToRgb(hex) {
    // Remove # if present
    hex = hex.replace('#', '')

    // Handle shorthand (e.g., #fff)
    if (hex.length === 3) {
      hex = hex.split('').map((c) => c + c).join('')
    }

    const r = parseInt(hex.substring(0, 2), 16)
    const g = parseInt(hex.substring(2, 4), 16)
    const b = parseInt(hex.substring(4, 6), 16)

    return `${r}, ${g}, ${b}`
  }

  /**
   * Get animation CSS with brand color
   */
  getAnimationStyles(primaryRgb) {
    return `
      :root { --ai-color-rgb: ${primaryRgb}; }

      @keyframes ai-shimmer-wave { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }
      @keyframes ai-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      @keyframes ai-edit-glow {
        0%, 100% { box-shadow: 0 0 0 2px rgba(${primaryRgb}, 0.2), 0 0 20px rgba(${primaryRgb}, 0.1); }
        50% { box-shadow: 0 0 0 3px rgba(${primaryRgb}, 0.4), 0 0 30px rgba(${primaryRgb}, 0.2); }
      }
      @keyframes ai-text-blur {
        0%, 100% { filter: blur(0px); opacity: 1; }
        50% { filter: blur(2px); opacity: 0.7; }
      }
      @keyframes ai-generate-breathe {
        0%, 100% { transform: scale(1); }
        50% { transform: scale(0.998); }
      }
      @keyframes ai-skeleton-shimmer { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }
      @keyframes ai-success-flash {
        0% { box-shadow: 0 0 0 0 rgba(16, 185, 129, 0); }
        50% { box-shadow: 0 0 0 4px rgba(16, 185, 129, 0.3), 0 0 20px rgba(16, 185, 129, 0.2); }
        100% { box-shadow: 0 0 0 0 rgba(16, 185, 129, 0); }
      }
      @keyframes ai-error-shake {
        0%, 100% { transform: translateX(0); }
        20%, 60% { transform: translateX(-4px); }
        40%, 80% { transform: translateX(4px); }
      }

      .ai-status-badge {
        position: absolute; top: 12px; left: 12px; z-index: 1000;
        display: flex; align-items: center; gap: 8px;
        padding: 8px 14px; background: rgba(255,255,255,0.95);
        backdrop-filter: blur(8px); border-radius: 9999px;
        box-shadow: 0 4px 12px rgba(0,0,0,0.15);
        font-family: system-ui, sans-serif; font-size: 13px; font-weight: 600;
        color: rgb(${primaryRgb});
      }
      .ai-status-badge .ai-spinner { width: 16px; height: 16px; animation: ai-spin 1s linear infinite; }
      .ai-status-badge.ai-status-error { background: rgba(254,242,242,0.95); color: #dc2626; }

      .ai-processing-edit { position: relative; animation: ai-edit-glow 2s ease-in-out infinite; }
      .ai-processing-edit::before {
        content: ''; position: absolute; inset: 0;
        background: linear-gradient(90deg, transparent 0%, rgba(${primaryRgb}, 0.05) 50%, transparent 100%);
        background-size: 200% 100%; animation: ai-shimmer-wave 2s ease-in-out infinite;
        pointer-events: none; z-index: 5;
      }

      .ai-processing-content { position: relative; }
      .ai-processing-content::before {
        content: ''; position: absolute; inset: 0;
        background: linear-gradient(90deg, transparent 0%, rgba(${primaryRgb}, 0.08) 25%, rgba(${primaryRgb}, 0.12) 50%, rgba(${primaryRgb}, 0.08) 75%, transparent 100%);
        background-size: 200% 100%; animation: ai-shimmer-wave 1.5s ease-in-out infinite;
        pointer-events: none; z-index: 5;
      }
      .ai-processing-content h1, .ai-processing-content h2, .ai-processing-content h3,
      .ai-processing-content h4, .ai-processing-content p, .ai-processing-content li,
      .ai-processing-content span:not(.ai-status-badge):not(.ai-status-badge *) {
        animation: ai-text-blur 2.5s ease-in-out infinite;
      }

      .ai-processing-generate { position: relative; animation: ai-generate-breathe 3s ease-in-out infinite; min-height: 200px; }
      .ai-processing-generate > *:not(.ai-status-badge):not(.ai-skeleton-overlay) { visibility: hidden !important; opacity: 0 !important; }

      .ai-skeleton-overlay {
        position: absolute; inset: 0; padding: 40px;
        display: flex; flex-direction: column; gap: 16px;
        background: linear-gradient(110deg, transparent 0%, rgba(${primaryRgb}, 0.03) 20%, rgba(${primaryRgb}, 0.05) 40%, rgba(${primaryRgb}, 0.03) 60%, transparent 100%);
        background-size: 300% 100%; animation: ai-shimmer-wave 2.5s linear infinite;
        z-index: 10;
      }
      .ai-skeleton-bar {
        background: linear-gradient(90deg, rgba(${primaryRgb}, 0.1) 0%, rgba(${primaryRgb}, 0.2) 50%, rgba(${primaryRgb}, 0.1) 100%);
        background-size: 200% 100%; animation: ai-skeleton-shimmer 1.5s ease-in-out infinite;
        border-radius: 6px;
      }
      .ai-skeleton-heading { height: 32px; width: 60%; margin-bottom: 8px; }
      .ai-skeleton-subheading { height: 20px; width: 80%; margin-bottom: 24px; }
      .ai-skeleton-line { height: 14px; width: 100%; margin-bottom: 8px; }
      .ai-skeleton-line:nth-child(even) { width: 90%; }
      .ai-skeleton-button { height: 44px; width: 160px; margin-top: 16px; border-radius: 8px; }

      .ai-processing-success { animation: ai-success-flash 0.6s ease-out forwards; }
      .ai-processing-error { animation: ai-error-shake 0.4s ease-out; box-shadow: 0 0 0 2px rgba(239, 68, 68, 0.5) !important; }
    `
  }

  /**
   * Apply processing animation to a block in the preview iframe
   */
  applyProcessingAnimation(blockId, mode) {
    this.ensureIframeStyles()

    const iframe = document.querySelector('#previewFrame, iframe[name="preview"]')
    if (!iframe?.contentDocument) return

    const block = iframe.contentDocument.querySelector(`[data-block-id="${blockId}"]`)
    if (!block) return

    // Remove any existing processing classes
    block.classList.remove('ai-processing-edit', 'ai-processing-content', 'ai-processing-generate')

    // Remove existing skeleton if any
    const existingSkeleton = block.querySelector('.ai-skeleton-overlay')
    existingSkeleton?.remove()

    // Add mode-specific class
    block.classList.add(`ai-processing-${mode}`)

    // For generate mode, add skeleton overlay
    if (mode === 'generate') {
      this.addSkeletonOverlay(block)
    }

    // Add status badge
    this.addProcessingBadge(block, mode)
  }

  /**
   * Add skeleton loader overlay for generate mode
   */
  addSkeletonOverlay(block) {
    const skeleton = document.createElement('div')
    skeleton.className = 'ai-skeleton-overlay'
    skeleton.innerHTML = `
      <div class="ai-skeleton-bar ai-skeleton-heading"></div>
      <div class="ai-skeleton-bar ai-skeleton-subheading"></div>
      <div class="ai-skeleton-bar ai-skeleton-line"></div>
      <div class="ai-skeleton-bar ai-skeleton-line"></div>
      <div class="ai-skeleton-bar ai-skeleton-line"></div>
      <div class="ai-skeleton-bar ai-skeleton-line" style="width: 70%;"></div>
      <div class="ai-skeleton-bar ai-skeleton-button"></div>
    `
    block.appendChild(skeleton)
  }

  /**
   * Add status badge to block
   */
  addProcessingBadge(block, mode) {
    // Remove existing badge if any
    const existingBadge = block.querySelector('.ai-status-badge')
    existingBadge?.remove()

    const badge = document.createElement('div')
    badge.className = 'ai-status-badge'
    badge.innerHTML = `
      <svg class="ai-spinner" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
        <circle cx="12" cy="12" r="10" opacity="0.25"/>
        <path d="M4 12a8 8 0 018-8" opacity="0.75" stroke-linecap="round"/>
      </svg>
      <span>${this.getModeLabel(mode)}</span>
    `
    block.insertBefore(badge, block.firstChild)
  }

  /**
   * Remove processing animation from block
   */
  removeProcessingAnimation(blockId) {
    const iframe = document.querySelector('#previewFrame, iframe[name="preview"]')
    if (!iframe?.contentDocument) return

    const block = iframe.contentDocument.querySelector(`[data-block-id="${blockId}"]`)
    if (!block) return

    block.classList.remove('ai-processing-edit', 'ai-processing-content', 'ai-processing-generate')

    // Remove skeleton overlay
    const skeleton = block.querySelector('.ai-skeleton-overlay')
    skeleton?.remove()

    // Remove status badge
    const badge = block.querySelector('.ai-status-badge')
    badge?.remove()
  }

  /**
   * Update sidebar block card to show processing state
   */
  updateBlockCardProcessingState(blockId, isProcessing, mode = null) {
    const card = document.querySelector(`.block-card[data-block-id="${blockId}"]`)
    if (!card) return

    const existingIndicator = card.querySelector('.ai-card-indicator')

    if (isProcessing) {
      // Add processing ring
      card.classList.add('ring-2', 'ring-purple-400', 'ring-offset-1')

      if (!existingIndicator) {
        const indicator = document.createElement('div')
        indicator.className = 'ai-card-indicator'
        indicator.innerHTML = `
          <div class="flex items-center gap-2 px-3 py-2 bg-purple-50 border-t border-purple-100">
            <svg class="w-4 h-4 text-purple-600 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
            </svg>
            <span class="text-xs font-medium text-purple-700">${this.getModeLabel(mode)}...</span>
          </div>
        `
        card.appendChild(indicator)
      }
    } else {
      existingIndicator?.remove()
      card.classList.remove('ring-2', 'ring-purple-400', 'ring-offset-1')
    }
  }

  /**
   * Get display label for AI mode
   */
  getModeLabel(mode) {
    const labels = {
      edit: 'AI Editing',
      content: 'Updating Content',
      generate: 'Regenerating'
    }
    return labels[mode] || 'Processing'
  }
}
